import React, { useState, useEffect, useRef } from 'react';
import SpecReview from '../components/SpecReview';
import usePersistedState from '../usePersistedState';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
API.interceptors.request.use(cfg => {
  try { const u = JSON.parse(localStorage.getItem('npd_user')); if (u?.token) cfg.headers.Authorization = 'Bearer ' + u.token; } catch {}
  return cfg;
});

export default function SpecSheets() {
  const [sheets, setSheets] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [reviewRev, setReviewRev] = useState(null);
  const _u = (() => { try { return JSON.parse(localStorage.getItem('npd_user')); } catch { return null; } })();
  const _role = _u?.role || 'standard';
  const canWriteSpecs = ['admin','standard','qc','qc_manager'].includes(_role) || _u?.username === 'admin';
  const _tok = _u?.token ? ('?token=' + encodeURIComponent(_u.token)) : '';
  const canConfirmSpecs = ['admin','standard','qc_manager','op_manager','approver'].includes(_role) || _u?.username === 'admin';
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = usePersistedState('specs.search', '');
  const [createForm, setCreateForm] = useState({ doc_number: '', product_name: '', brand: '', group_code: '', category_code: '', item_code: '', template_id: '' });
  const [tplList, setTplList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filter, setFilter] = usePersistedState('specs.filter', 'all');
  const [selectedFile, setSelectedFile] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const fileRef = useRef();
  const fileForUpload = useRef(null);

  const [form, setForm] = useState({
    group_code: '', category_code: '', product_name: '',
    brand: '', item_code: '', doc_number: '', notes: ''
  });

  useEffect(() => { loadSheets(); }, []);

  const location = useLocation();
  React.useEffect(() => {
    const sid = location.state?.openSheetId;
    if (sid) {
      API.get('/spec-sheets/' + sid).then(r => setSelected(r.data)).catch(() => {});
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const loadSheets = () => {
    API.get('/spec-sheets').then(r => setSheets(r.data)).catch(() => {});
  };

  const loadSheet = async (s) => {
    try {
      const r = await API.get('/spec-sheets/' + s.id);
      setSelected(r.data);
    } catch(e) {
      setSelected(s);
    }
  };

  const generateDocNumber = async () => {
    if (!form.group_code || !form.category_code) return;
    const r = await API.get('/spec-sheets/generate-number', { params: { group_code: form.group_code, category_code: form.category_code } });
    setForm(f => ({ ...f, doc_number: r.data.doc_number }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    // Auto-suggest product name from filename
    const name = file.name.replace('.docx','').replace('.doc','').replace('.DOCX','').replace('.DOC','');
    if (!form.product_name && name) {
      setForm(f => ({...f, product_name: name}));
    }
  };

  const handleUpload = async () => {
    console.log("Upload clicked, file:", fileForUpload.current?.name, "selectedFile:", selectedFile?.name, "doc:", form.doc_number);
    const file = fileForUpload.current || selectedFile;
    if (!file) { setError('Please select a file'); return; }
    if (!form.doc_number) { setError('Please fill Group Code, Category Code and click Generate'); return; }
    if (!form.group_code || !form.category_code) { setError('Group Code and Category Code are required'); return; }
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.keys(form).forEach(k => fd.append(k, form[k]));
      await API.post('/spec-sheets/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess('File uploaded! PDF generated automatically — click View to review before confirming.');
      setShowUpload(false);
      setSelectedFile(null);
      setForm({ group_code: '', category_code: '', product_name: '', brand: '', item_code: '', doc_number: '', notes: '' });
      loadSheets();
    } catch (err) {
      console.error("Upload error:", err);
      setError('Upload failed: ' + (err.response?.data?.error || err.message) + ' (status: ' + (err.response?.status || 'no response') + ')');
    }
    setUploading(false);
  };

  const handleConfirm = async (sheet, rev) => {
    setConfirming(rev.id); setError(null);
    try {
      await API.post('/spec-sheets/' + sheet.id + '/confirm', { rev_id: rev.id, confirmed_by: 'admin' });
      setSuccess('Converted to PDF successfully!');
      loadSheets();
      setSelected(null);
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.error || 'Conversion failed — make sure LibreOffice is installed on server');
    }
    setConfirming(null);
  };

  const filtered = sheets.filter(s => filter === 'all' ? true : s.status === filter)
    .filter(s => {
      if (!search.trim()) return true;
      const hay = ((s.doc_number||'') + ' ' + (s.product_name||'') + ' ' + (s.brand||'') + ' ' + (s.item_code||'')).toLowerCase();
      return hay.includes(search.trim().toLowerCase());
    });
  const pending = sheets.filter(s => s.status === 'pending').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Spec Sheets</div>
          <div className="page-subtitle">Product specification documents — upload, version and manage</div>
        </div>
        {canWriteSpecs && <button className="btn btn-primary" onClick={() => setShowUpload(true)}>+ Upload Spec Sheet</button>}
          {canWriteSpecs && <button className="btn btn-secondary" style={{ marginLeft: 8 }} onClick={() => { setShowCreate(true); API.get('/spec-templates').then(r => setTplList(r.data)).catch(() => {}); }}>+ Create from Template</button>}
      </div>

      {pending > 0 && (
        <div style={{ background: '#fff3e0', border: '1px solid #f5d898', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#e67e00' }}>
          ⏳ <strong>{pending} pending</strong> spec sheet{pending > 1 ? 's' : ''} — review and confirm to convert to PDF
        </div>
      )}

      {error && <div style={{ background: '#fdf0ef', color: '#c0392b', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error} <button onClick={() => setError(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button></div>}
      {success && <div style={{ background: '#f0faf0', color: '#2e7d32', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>✅ {success} <button onClick={() => setSuccess(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button></div>}

      {/* Filter tabs */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        <input className="form-input" placeholder="🔍 Search doc no, product, brand..." value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 280, marginRight: 12, padding: '6px 12px', fontSize: 13 }} />
        {[['all','All'],['pending','Pending'],['confirmed','Confirmed']].map(([v,l]) => (
          <button key={v} className={`tab${filter===v?' active':''}`} onClick={() => setFilter(v)}>{l}</button>
        ))}
      </div>

      {/* Spec Sheets Table */}
      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📄</div>
            <p>No spec sheets yet. Upload your first one!</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Doc Number</th>
                  <th>Product Name</th>
                  <th>Brand/Group</th>
                  <th>Item Code</th>
                  <th>Rev</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Linked Product</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.id}>
                    <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{s.doc_number}</span></td>
                    <td style={{ fontSize: 13 }}>{s.product_name}</td>
                    <td style={{ fontSize: 12 }}>{s.brand} <span style={{ color: 'var(--text-muted)' }}>{s.group_code}</span></td>
                    <td><span style={{ fontFamily: 'monospace', fontSize: 12 }}>{s.item_code || '—'}</span></td>
                    <td style={{ textAlign: 'center' }}>{s.rev_number ? 'Rev ' + s.rev_number : '—'}</td>
                    <td>
                      <span className={s.status === 'confirmed' ? 'badge badge-recipe_confirmed' : 'badge badge-trial'}>
                        {s.status === 'confirmed' ? '✅ Confirmed' : '⏳ Pending'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>{s.rev_date ? new Date(s.rev_date).toLocaleDateString('en-AE') : '—'}</td>
                    <td style={{ fontSize: 12 }}>{s.product_name_linked || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary btn-sm" onClick={async () => { try { const r = await API.get("/spec-sheets/" + s.id); setSelected(r.data); } catch(e) { setSelected(s); } }}>View</button>
                        {s.status === 'pending' && (
                          <button className="btn btn-primary btn-sm" onClick={async () => { try { const r = await API.get("/spec-sheets/" + s.id); setSelected(r.data); } catch(e) { setSelected(s); } }}>Review</button>
                        )}
                        {s.pdf_file && s.latest_rev_id && (
                          <a href={`/api/spec-sheets/${s.id}/download/${s.latest_rev_id}${_tok}`} className="btn btn-secondary btn-sm" target="_blank" rel="noopener noreferrer">PDF</a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <div className="modal-header">
              <div className="modal-title">Upload Spec Sheet</div>
              <button className="modal-close" onClick={() => setShowUpload(false)}>✕</button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Group / Brand Code *</label>
                <input type="text" placeholder="e.g. WA, BA, KR" maxLength={4}
                  value={form.group_code} onChange={e => setForm({...form, group_code: e.target.value.toUpperCase()})} />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>WA=Warmleys, BA=Bayara, KR=Kareem</div>
              </div>
              <div className="form-group">
                <label>Category Code *</label>
                <input type="text" placeholder="e.g. AL, CA, DA" maxLength={4}
                  value={form.category_code} onChange={e => setForm({...form, category_code: e.target.value.toUpperCase()})} />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>AL=Almond, CA=Cashew, DA=Dates</div>
              </div>
              <div className="form-group full">
                <label>Document Number</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" value={form.doc_number} onChange={e => setForm({...form, doc_number: e.target.value})}
                    placeholder="Auto-generate or type manually" style={{ flex: 1, fontFamily: 'monospace' }} />
                  <button className="btn btn-secondary" onClick={generateDocNumber} disabled={!form.group_code || !form.category_code}>
                    Generate
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Product Name</label>
                <input type="text" placeholder="e.g. Almond Jumbo" value={form.product_name}
                  onChange={e => setForm({...form, product_name: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Brand</label>
                <input type="text" placeholder="e.g. Warmleys" value={form.brand}
                  onChange={e => setForm({...form, brand: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Item Code (links to product)</label>
                <input type="text" placeholder="e.g. KARM-001" value={form.item_code}
                  onChange={e => setForm({...form, item_code: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <input type="text" placeholder="Optional notes for this revision" value={form.notes}
                  onChange={e => setForm({...form, notes: e.target.value})} />
              </div>
            </div>
            <div style={{ margin: '16px 0', padding: 16, background: 'var(--bg-elevated)', borderRadius: 8, textAlign: 'center', border: '2px dashed var(--border)' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Select Word file (.docx)</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Will be converted to PDF on confirmation</div>
              <input type="file" accept=".docx,.doc" onChange={handleFileSelect} 
                style={{ display: 'block', width: '100%', padding: '8px', border: '1px solid var(--border)', borderRadius: 8 }} />
              {selectedFile && <div style={{fontSize:12,color:'var(--accent)',marginTop:4}}>📄 {selectedFile.name}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowUpload(false); setSelectedFile(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || !selectedFile}>
                {uploading ? 'Uploading...' : '📤 Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View/Review Modal */}
      {selected && (
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <div className="modal-header">
              <div>
                <div className="modal-title">{selected.doc_number}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{selected.product_name} · {selected.brand}</div>
              </div>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
              {[['Document No', selected.doc_number], ['Group Code', selected.group_code], ['Category', selected.category_code],
                ['Brand', selected.brand], ['Item Code', selected.item_code||'—'], ['Status', selected.status]].map(([l,v]) => (
                <div key={l} style={{ background: 'var(--bg-elevated)', padding: '8px 12px', borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{l}</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Revisions */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Revisions</div>
              {selected.revisions && selected.revisions.length > 0 ? selected.revisions.map(rev => (
                <div key={rev.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 6 }}>
                  <div>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent)' }}>Rev {rev.rev_number}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>{new Date(rev.created_at).toLocaleDateString('en-AE')}</span>
                    {rev.notes && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>{rev.notes}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {rev.word_file && (
                      <a href={`/api/spec-sheets/${selected.id}/word/${rev.id}${_tok}`} className="btn btn-secondary btn-sm" target="_blank" rel="noopener noreferrer">
                        📄 View Word
                      </a>
                    )}
                    {(rev.word_file || rev.template_id || rev.extracted_json) && (
                      <button className="btn btn-primary btn-sm" onClick={() => setReviewRev({ sheetId: selected.id, revId: rev.id })}>
                        {rev.extracted_json ? '👁 View Spec' : '📋 Extract'}
                      </button>
                    )}
                    {!rev.confirmed_at && canConfirmSpecs && (
                      <button className="btn btn-primary btn-sm" onClick={() => rev.extracted_json ? setReviewRev({ sheetId: selected.id, revId: rev.id }) : handleConfirm(selected, rev)} disabled={confirming === rev.id}>
                        {confirming === rev.id ? 'Confirming...' : '✅ Confirm'}
                      </button>
                    )}
                    {rev.pdf_file && (
                      <a href={`/api/spec-sheets/${selected.id}/download/${rev.id}${_tok}`} className="btn btn-primary btn-sm" target="_blank" rel="noopener noreferrer">
                        📥 Download PDF
                      </a>
                    )}
                    <span className={rev.pdf_file ? 'badge badge-recipe_confirmed' : 'badge badge-trial'}>
                      {rev.pdf_file ? '✅ PDF Ready' : '⏳ Word Only'}
                    </span>
                  </div>
                </div>
              )) : <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No revisions yet</div>}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header"><div className="modal-title">Create Spec from Template</div><button className="modal-close" onClick={() => setShowCreate(false)}>×</button></div>
            <div style={{ display: 'grid', gap: 10, padding: '4px 0' }}>
              <select className="form-input" value={createForm.template_id} onChange={e => setCreateForm(f => ({ ...f, template_id: e.target.value }))}>
                <option value="">— Choose format/template —</option>
                {tplList.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <input className="form-input" placeholder="Doc Number (e.g. KFI-RD-WA-XX-01)" value={createForm.doc_number} onChange={e => setCreateForm(f => ({ ...f, doc_number: e.target.value }))} />
              <input className="form-input" placeholder="Product Name" value={createForm.product_name} onChange={e => setCreateForm(f => ({ ...f, product_name: e.target.value }))} />
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-input" style={{ flex: 1 }} placeholder="Brand" value={createForm.brand} onChange={e => setCreateForm(f => ({ ...f, brand: e.target.value }))} />
                <input className="form-input" style={{ width: 110 }} placeholder="Group (WA)" value={createForm.group_code} onChange={e => setCreateForm(f => ({ ...f, group_code: e.target.value }))} />
                <input className="form-input" style={{ width: 110 }} placeholder="Category (AL)" value={createForm.category_code} onChange={e => setCreateForm(f => ({ ...f, category_code: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={async () => {
                if (!createForm.template_id || !createForm.doc_number) { setError('Template and Doc Number are required'); return; }
                try {
                  const r = await API.post('/spec-sheets/create-blank', createForm);
                  setShowCreate(false);
                  setCreateForm({ doc_number: '', product_name: '', brand: '', group_code: '', category_code: '', item_code: '', template_id: '' });
                  loadSheets();
                  setReviewRev({ sheetId: r.data.spec_sheet_id, revId: r.data.rev_id });
                } catch (e) { setError(e.response?.data?.error || 'Create failed'); }
              }}>Create & Fill</button>
            </div>
          </div>
        </div>
      )}
            {reviewRev && (
        <SpecReview sheetId={reviewRev.sheetId} revId={reviewRev.revId} onClose={() => { setReviewRev(null); loadSheets(); if (selected) loadSheet(selected); }} />
      )}
    </div>
  );
}
// v2
